import { NotifyHelper } from 'classes/helpers/notify.helper';
import {
  ContactQuality,
  PitchLocation,
  RecapColorBy,
  RecapFieldMode,
  RecapZoneOverlay,
  SwingDecision,
  ZoneMetric,
} from 'enums/session-recap/enums';
import { IRecapZoneFilter } from 'interfaces/session-recap/i-recap-zone-filter';
import { OutcomeCode } from 'lib_ts/enums/hitters.enums';
import { PitchType, PitcherHand } from 'lib_ts/enums/pitches.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { IHitterSessionAdvancedFilters } from 'lib_ts/interfaces/session-hits/i-hitter-session-filters';
import {
  IDetectionIssue,
  ISessionHit,
  IUpdateHitRequest,
} from 'lib_ts/interfaces/session-hits/i-session-hit';
import {
  ISHSummary,
  ISHSummaryRequest,
} from 'lib_ts/interfaces/session-hits/i-summary';
import { clone } from 'lodash';
import { HittersService } from 'services/hitters.service';
import { MachinesService } from 'services/machines.service';
import { create } from 'zustand';

export interface ISessionsFilter {
  hitterIDs: string[];
  startDates: string[];
  isInGame: Nullable<boolean>;
}

interface ViewState {
  hoveredHitID: string | null;
  selectedHitID: string | null;
  colorByID: RecapColorBy;
  fieldMode: RecapFieldMode;
  zoneOverlayID: RecapZoneOverlay;
  zoneMetricID: ZoneMetric;
  showShots: boolean;
  /** null shows every legend category; a list isolates those keys on the charts */
  legendKeys: string[] | null;
  /** null shows every pitch; a zone keeps chart dots that fall in it */
  zoneFilter: IRecapZoneFilter | null;

  newestHitID: string | null;
  updatedHitID: string | null;
  refetchKey: number;
}

interface DataState {
  sessions: ISHSummary[];
  hits: ISessionHit[];
  // selecting a session will fetch the current state of the session's machine (may not be the machine that the user is currently using)
  // mainly used for deciding whether to convert units to metric vs imperial
  sessionMachine: IMachine | null;
}

interface FilterState {
  // which sessions to show
  sessionsFilter: ISessionsFilter;

  pitchTypeFilter: PitchType[];
  outcomeFilter: OutcomeCode[];
  pitcherHandFilter: PitcherHand[];
  pitchLocationFilter: PitchLocation[];
  contactQualityFilter: ContactQuality[];
  swingFilter: SwingDecision[];
  advancedFilters: IHitterSessionAdvancedFilters | null;
}

type State = ViewState &
  DataState &
  FilterState & {
    loadingSessions: boolean;
    loadingHits: boolean;

    error: Nullable<string>;
  };

interface DataActions {
  // Data fetchers
  fetchSessions: (payload: ISHSummaryRequest) => Promise<ISHSummary[]>;
  fetchHitsAndMachine: (
    machineID: string,
    sessionID: string
  ) => Promise<ISessionHit[]>;
  fetchHit: (id: string) => Promise<Nullable<ISessionHit>>;
  addAnnotatedVideo: (hitID: string, videoID: string) => Promise<void>;
  addReplayVideo: (hitID: string, videoID: string) => Promise<void>;

  // Update a single record
  updateHit: (id: string, value: IUpdateHitRequest) => Promise<void>;

  // remove one or more sessions
  deleteSessions: (ids: string[]) => Promise<void>;
}

type Actions = DataActions & {
  reset: () => void;

  reportDetectionIssue: (
    hitID: string,
    detectionIssue: Partial<IDetectionIssue>
  ) => Promise<void>;
};

type AnalyticsStore = State & Actions;

const DEFAULT_HIT_FILTERS: Pick<
  State,
  | 'pitchTypeFilter'
  | 'outcomeFilter'
  | 'pitcherHandFilter'
  | 'pitchLocationFilter'
  | 'contactQualityFilter'
  | 'swingFilter'
  | 'advancedFilters'
> = {
  pitchTypeFilter: [],
  outcomeFilter: [],
  pitcherHandFilter: [],
  pitchLocationFilter: [],
  contactQualityFilter: [],
  swingFilter: [],
  advancedFilters: null,
};

const DEFAULT_VIEW_STATE: ViewState = {
  hoveredHitID: null,
  selectedHitID: null,
  colorByID: RecapColorBy.Result,
  fieldMode: RecapFieldMode.Spray,
  zoneOverlayID: RecapZoneOverlay.None,
  zoneMetricID: ZoneMetric.PitchRate,
  showShots: true,
  legendKeys: null,
  zoneFilter: null,
  newestHitID: null,
  updatedHitID: null,
  refetchKey: Date.now(),
};

const DEFAULT_STATE: State = {
  loadingSessions: false,
  loadingHits: false,

  error: null,

  sessions: [],
  hits: [],
  sessionMachine: null,

  sessionsFilter: {
    hitterIDs: [],
    startDates: [],
    isInGame: null,
  },

  ...DEFAULT_HIT_FILTERS,
  ...DEFAULT_VIEW_STATE,
};

const useAnalyticsStore = create<AnalyticsStore>((set, get) => ({
  ...DEFAULT_STATE,
  fetchSessions: async (payload) => {
    set({ loadingSessions: true, error: null });

    try {
      const response =
        await HittersService.getInstance().getSessionSummaries(payload);

      set({
        loadingSessions: false,
        sessions: response,
      });

      return response;
    } catch (error) {
      set({
        error: 'Failed to fetch machine sessions',
        loadingSessions: false,
      });
      return [];
    }
  },
  // Loads / resets the hitter session table
  fetchHitsAndMachine: async (machineID, sessionID) => {
    set({
      loadingHits: true,
      error: null,
      hits: [],
      // Reset filters and selection when we change sessions
      ...DEFAULT_HIT_FILTERS,
      hoveredHitID: null,
      selectedHitID: null,
      legendKeys: null,
      zoneFilter: null,
    });

    try {
      const [machine, hits] = await Promise.all([
        MachinesService.getInstance()
          .getByMachineID(machineID)
          .then((m) => m.machine),
        HittersService.getInstance().getSessionHits(sessionID),
      ]);

      set({
        loadingHits: false,
        hits: hits,
        sessionMachine: machine,
      });

      return hits;
    } catch (error) {
      set({ error: 'Failed to fetch hitter session', loadingHits: false });
      return [];
    }
  },
  fetchHit: async (id) => {
    const { hits } = get();

    try {
      const hit = await HittersService.getInstance().getHit(id);

      if (!hit) {
        return null;
      }

      // Update existing hit in store if we already have it
      if (hits.find((h) => h._id === id)) {
        set({
          hits: hits.map((h) => (h._id === id ? hit : h)),
        });
        return hit;
      }

      set({
        hits: [...hits, hit],
      });

      return hit;
    } catch (error) {
      set({ error: 'Failed to fetch hit', loadingHits: false });
      return null;
    }
  },
  reportDetectionIssue: async (
    hitID: string,
    detectionIssue: Partial<IDetectionIssue>
  ) => {
    const updatedHit = await HittersService.getInstance().upsertDetectionIssue(
      hitID,
      detectionIssue
    );

    if (!updatedHit) {
      return;
    }

    set((state) => ({
      hits: state.hits.map((h) => {
        if (h._id !== hitID) {
          return h;
        }

        // spread instead of replace since updatedHit won't include mixed-in tracking_device from $lookup
        return {
          ...h,
          ...updatedHit,
        };
      }),
    }));
  },
  addAnnotatedVideo: async (hitID: string, videoID: string) => {
    const { hits } = get();

    try {
      set({
        hits: hits.map((h) => {
          if (h._id !== hitID) {
            return h;
          }

          return {
            ...h,
            fire: {
              ...h.fire,
              annotated_video_id: videoID,
            },
          };
        }),
      });
    } catch (error) {
      set({ error: 'Failed to update hit with annotated video' });
    }
  },
  addReplayVideo: async (hitID: string, videoID: string) => {
    const { hits } = get();

    try {
      set({
        hits: hits.map((h) => {
          if (h._id !== hitID) {
            return h;
          }

          return {
            ...h,
            fire: {
              ...h.fire,
              replay_video_id: videoID,
            },
          };
        }),
      });
    } catch (error) {
      set({ error: 'Failed to update hit with replay video' });
    }
  },
  updateHit: async (id, value) => {
    try {
      const result = await HittersService.getInstance().updateHit(id, value);

      set((state) => {
        const hitIndex = state.hits.findIndex((h) => h._id === id);

        if (hitIndex === -1) {
          return state;
        }

        // this ensures that the reference to this specific hit doesn't change, preventing unnecessary re-renders
        const next = [...state.hits];
        next[hitIndex] = { ...state.hits[hitIndex], ...result };

        return { hits: next };
      });
    } catch (error) {
      set({ error: 'Failed to update hit' });
    }
  },
  deleteSessions: async (ids) => {
    try {
      const success = await HittersService.getInstance().deleteSessions(ids);

      if (!success) {
        NotifyHelper.error({
          message_md: `There was a problem deleting the ${
            ids.length > 1 ? 'reports' : 'report'
          }.`,
        });
        return;
      }

      NotifyHelper.success({
        delay_ms: 3_000,
        message_md:
          ids.length > 1
            ? 'Selected reports deleted successfully.'
            : 'Report deleted successfully!',
      });

      set((prev) => ({
        // keep only the sessions that were not deleted
        sessions: prev.sessions.filter((m) => !ids.includes(m._id)),
      }));
    } catch (error) {
      set({ error: 'Failed to delete sessions' });
    }
  },

  reset: () => set(clone(DEFAULT_STATE)),
}));

export default useAnalyticsStore;
